// packages/shared/src/schemas/widget-interaction-state.ts
//
// Phase 11.3 Plan 02 — WidgetInteractionStateSchema.
//
// The runtime validation contract for the per-recipe-per-user interaction-state
// blob: `Record<WidgetInstanceId, WidgetState>`, serialized from the renderer's
// in-memory Map and persisted server-side via PUT /api/recipes/:id/state. This
// schema is the route-boundary gate (HTTP 422 INVALID_BODY) that rejects
// malformed / smuggled / oversized state before any DB write.
//
// ---------------------------------------------------------------------------
// WHY z.union (NOT z.discriminatedUnion)
// ---------------------------------------------------------------------------
// The TS source-of-truth `WidgetState`
// (apps/web/src/widgets/widget-renderer-props.ts:24-32) has NO shared literal
// discriminator field — its variants are distinguished STRUCTURALLY by their
// keys (`selected` / `checkedItems`+`viewMode` / `selectedStepId` /
// `selectedVariantByOptionId`). Zod 4's `z.discriminatedUnion` requires a
// `z.literal(...)` discriminator per variant; these variants have none. This is
// exactly the precedent documented in `constraint.ts:107,143-148` (the
// LeafPredicate / Predicate union): use `z.union([...])` and let Zod's runtime
// dispatch be structural matching (it tries each variant, first match wins,
// fails if none match). `z.strictObject` per variant means a smuggled extra key
// (e.g. `{ selected, checkedItems }`) matches NO variant and is rejected.
//
// ---------------------------------------------------------------------------
// WHY no z.undefined() union member (undefined-state widgets omit their key)
// ---------------------------------------------------------------------------
// `WidgetState` includes `| undefined` (static-note + ranked-tier carry no own
// state). We deliberately do NOT add a `z.undefined()` branch to the union. Per
// CONTEXT specifics §, undefined-state widgets are represented by OMITTING their
// key from the record — Plan 04's Map→Record serializer (`Object.fromEntries`)
// drops entries whose value is `undefined`. So the record never carries an
// `undefined` value, and the union is the union of DEFINED states only.
//
// ---------------------------------------------------------------------------
// SIZE BOUNDS (.max()) — layered with the DB CHECK
// ---------------------------------------------------------------------------
// Plan 11.3-01 fixed the DB CHECK cap at 16384 bytes (16 KB) — measured
// worst-case fully-interacted blob = 2288 bytes (~7.2x headroom). The Zod
// `.max()` array/record bounds below back that DB CHECK: a record at these
// bounds still serializes well under 16384 bytes, so the friendlier Zod 422
// fires BEFORE the DB 23514 check_violation. Per-string `.max(200)` matches the
// established id-length discipline (summary-checklist.ts ids are max 100-200).
// Global nesting depth is already covered by the route's jsonDepthGate(50) — not
// re-implemented here.
//
// isolatedDeclarations discipline (mirrors summary-checklist.ts:38-51,113-138 +
// branch-variant.ts:42-77): `export interface`/`export type` + an explicit
// `z.ZodType<...>` annotation on each exported const so tsc emits declarations
// without reading across module boundaries (no TS9013).

import { z } from "zod";

// ---------------------------------------------------------------------------
// Bounds — the single source of truth for the array/record size caps.
// ---------------------------------------------------------------------------
// MAX_ENTRY_ID_LEN — widget-instance-id key length + per-string value length.
const MAX_ENTRY_ID_LEN = 200;
// MAX_SELECTED — option-set `selected` array bound. The worst-case fixture
// selects 13 options; 500 leaves wide headroom for pancake-class recipes while // grep-allow: pancake
// keeping a full record under the 16 KB DB CHECK.
const MAX_SELECTED = 500;
// MAX_CHECKED_ITEMS — summary-checklist + per-option-detail-card `checkedItems`.
// Worst-case modeled blob = 91 composite ids (13 flavors × 7 sections); 1000
// leaves headroom while staying under cap.
const MAX_CHECKED_ITEMS = 1000;
// MAX_VARIANT_ENTRIES — branch-variant `selectedVariantByOptionId` record size.
const MAX_VARIANT_ENTRIES = 500;
// MAX_AXIS_ENTRIES — scaling-matrix `axisSelections` (axisId→stepId) record size.
// Matches the scaling-matrix config schema `axes.max(4)` cap: a recipe declares at
// most 4 axes, so at most 4 axis→step selections can exist. Bounds the record at
// the source-of-truth value (DoS defense T-11.4.01-02).
const MAX_AXIS_ENTRIES = 4;
// MAX_RECORD_ENTRIES — number of widget-state entries in the outer record.
// One per interactive widget instance; 500 is far above any pancake-class // grep-allow: pancake
// recipe's widget count and keeps a bounded record under the DB CHECK.
const MAX_RECORD_ENTRIES = 500;
// MAX_CHOSEN_ROW_ENTRIES — option-tier-matrix `chosenRowByOptionId` record size
// (optionId→rowId). Mirrors the MAX_VARIANT_ENTRIES rationale: one entry per
// selected option in the source option-set; 500 leaves wide headroom over the
// worst-case option count while keeping a bounded record under the 16 KB DB
// CHECK (DoS defense T-11.5.01-DOS).
const MAX_CHOSEN_ROW_ENTRIES = 500;

const _idString = z.string().min(1).max(MAX_ENTRY_ID_LEN);

// ---------------------------------------------------------------------------
// WidgetState — the DEFINED variants (undefined omits its key; see header).
// Mirrors widget-renderer-props.ts:24-32 structurally.
// ---------------------------------------------------------------------------
export type WidgetState =
  | { selected: ReadonlyArray<string> } // option-set
  | {
      checkedItems: ReadonlyArray<string>;
      viewMode: "title" | "snippet" | "full" | "expanded";
    } // summary-checklist (viewMode = REND-04 collapsible open/closed state)
  | { selectedStepId: string } // size-and-yield
  | { selectedVariantByOptionId: Readonly<Record<string, string>> } // branch-variant
  | {
      axisSelections: Readonly<Record<string, string>>;
      multiplier: number;
      series?: { id: string; index: number } | undefined;
    } // Phase 11.4: scaling-matrix (byte-identical to widget-renderer-props.ts; series carries `| undefined` for exactOptionalPropertyTypes D-15)
  | { chosenRowByOptionId: Readonly<Record<string, string>> } // Phase 11.5: option-tier-matrix
  | { checkedItems: ReadonlyArray<string> }; // Phase 11.5: per-option-detail-card (bare — no viewMode; disjoint from summary-checklist)

// option-set
const _optionSetStateSchema = z.strictObject({
  selected: z.array(_idString).max(MAX_SELECTED),
});

// summary-checklist — viewMode enum matches summary-checklist.ts:154.
const _summaryChecklistStateSchema = z.strictObject({
  checkedItems: z.array(_idString).max(MAX_CHECKED_ITEMS),
  viewMode: z.enum(["title", "snippet", "full", "expanded"]),
});

// size-and-yield
const _sizeAndYieldStateSchema = z.strictObject({
  selectedStepId: _idString,
});

// branch-variant — z.record(z.string(), …) precedent: branch-variant.ts:24,76.
// The inner record is entry-count-bounded (.refine) so a single branch-variant
// entry cannot smuggle an unbounded option→variant map past the size layers.
const _branchVariantStateSchema = z.strictObject({
  selectedVariantByOptionId: z
    .record(_idString, _idString)
    .refine((rec) => Object.keys(rec).length <= MAX_VARIANT_ENTRIES, {
      message: `selectedVariantByOptionId may contain at most ${MAX_VARIANT_ENTRIES} entries`,
    }),
});

// scaling-matrix (Phase 11.4) — axisSelections is an axisId→stepId record bounded
// by MAX_AXIS_ENTRIES (mirrors the _branchVariantStateSchema entry-cap pattern).
// multiplier is z.number().int().min(0): a fractional/NaN multiplier is rejected
// (the stepper commits floored integers, REND-08) AND a negative multiplier is
// rejected — the config-schema bound floor is nonnegative (_boundSchema.min) and
// the stepper only ever commits clamped values in [min,max] with min ≥ 0, so the
// persisted-state gate must refuse a value its own producers cannot emit (a
// crafted PUT /state body with a negative multiplier no longer passes the route).
// series is an OPTIONAL strictObject (the chosen predefined-series id + integer
// index). The disjoint key set { axisSelections, multiplier } keeps
// structural-union dispatch unambiguous (T-11.4.01-03); strictObject refuses any
// smuggled extra key.
const _scalingMatrixStateSchema = z.strictObject({
  axisSelections: z
    .record(_idString, _idString)
    .refine((rec) => Object.keys(rec).length <= MAX_AXIS_ENTRIES, {
      message: `axisSelections may contain at most ${MAX_AXIS_ENTRIES} entries`,
    }),
  multiplier: z.number().int().min(0),
  series: z.strictObject({ id: _idString, index: z.number().int() }).optional(),
});

// option-tier-matrix (Phase 11.5) — chosenRowByOptionId is an optionId→rowId
// record (the single selected row per option in the source option-set), bounded
// by MAX_CHOSEN_ROW_ENTRIES via the _branchVariantStateSchema entry-cap pattern.
// Row ids are string-only (the config schema forbids numeric row ids). The
// disjoint key set { chosenRowByOptionId } keeps structural-union dispatch
// unambiguous; strictObject refuses any smuggled extra key.
const _optionTierMatrixStateSchema = z.strictObject({
  chosenRowByOptionId: z
    .record(_idString, _idString)
    .refine((rec) => Object.keys(rec).length <= MAX_CHOSEN_ROW_ENTRIES, {
      message: `chosenRowByOptionId may contain at most ${MAX_CHOSEN_ROW_ENTRIES} entries`,
    }),
});

// per-option-detail-card (Phase 11.5) — bare checkedItems of composite ids
// (`optionId::sectionId::blockId` or `::itemIndex`). REUSES MAX_CHECKED_ITEMS
// (Phase 11.3 already reserved it, naming per-option-detail-card). This variant
// DROPS summary-checklist's viewMode, giving it a required-key set disjoint from
// _summaryChecklistStateSchema's { checkedItems, viewMode }: a bare { checkedItems }
// blob fails summary-checklist's strictObject (no viewMode) and a { checkedItems,
// viewMode } blob fails this variant's strictObject (extra viewMode key), so the
// structural dispatch is unambiguous regardless of union order (RESEARCH Pitfall 1).
// MUST stay z.strictObject (NOT z.object) — non-strict breaks the disjointness.
const _perOptionDetailCardStateSchema = z.strictObject({
  checkedItems: z.array(_idString).max(MAX_CHECKED_ITEMS),
});

// z.union (NOT z.discriminatedUnion — see header). The seven variants share no
// literal discriminator; structural matching dispatches.
export const WidgetStateSchema: z.ZodType<WidgetState> = z.union([
  _optionSetStateSchema,
  _summaryChecklistStateSchema,
  _sizeAndYieldStateSchema,
  _branchVariantStateSchema,
  _scalingMatrixStateSchema,
  _optionTierMatrixStateSchema,
  _perOptionDetailCardStateSchema,
]);

// ---------------------------------------------------------------------------
// WidgetInteractionStateSchema — the persisted blob: Record<WidgetInstanceId,
// WidgetState>. Keys are widget-instance ids (non-empty, bounded); values are
// the WidgetState union. `.max(MAX_RECORD_ENTRIES)` bounds the entry
// count so a record at bounds stays under the 16 KB DB CHECK.
// ---------------------------------------------------------------------------
export const WidgetInteractionStateSchema: z.ZodType<Record<string, WidgetState>> = z
  .record(z.string().min(1).max(MAX_ENTRY_ID_LEN), WidgetStateSchema)
  .refine((rec) => Object.keys(rec).length <= MAX_RECORD_ENTRIES, {
    message: `state may contain at most ${MAX_RECORD_ENTRIES} widget entries`,
  });
